import { AUTH_ENDPOINTS } from "./endpoints";
import { useApiClient } from "../apiClients";
import type { CoreUser, PatchMeInput, RegisterResult } from "./types";
import i18n from "../../i18n";

const getLang = (): string => {
    const lang = i18n.language || window.localStorage?.getItem("lang") || "fr";
    return ["fr", "en", "ar"].includes(lang) ? lang : "fr";
};

interface RegisterPayload {
    login: string;
    email: string;
    password: string;
    personalInfo?: {
        firstname: string;
        lastname: string;
        birthdate: string;
    };
    businessInfo?: {
        taxeregistry: string;
        name: string;
    };
    metadata?: {
        pays?: string;
        inscription_date?: string;
    };
}

export const useAuthApi = () => {
    // If your identity service has a key (e.g. "identity"), use it here.
    // Otherwise use the default client.
    const api = useApiClient("identity");

    const loginBasic = async (username: string, password: string): Promise<boolean> => {
        await api.post(AUTH_ENDPOINTS.LOGIN, null, {
            auth: { username, password },
        });
        try {
            await me(); // sanity check session
            return true;
        } catch {
            return false;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await api.post(AUTH_ENDPOINTS.LOGOUT);
        } catch {
            /* no-op */
        }
    };

    const me = async <T extends CoreUser = CoreUser>(): Promise<T> => {
        const { data } = await api.get<T>(AUTH_ENDPOINTS.ME);
        return data;
    };

    const patchMe = async <T extends CoreUser = CoreUser>(input: PatchMeInput): Promise<T> => {
        const { data } = await api.patch<T>(AUTH_ENDPOINTS.ME, input);
        return data;
    };

    const updateMetadataKey = async <T extends CoreUser = CoreUser>(
        key: string,
        value: unknown
    ): Promise<T> => {
        return patchMe<T>({ metadata: { [key]: value } });
    };

    const isAuthenticated = async (): Promise<boolean> => {
        try {
            await me();
            return true;
        } catch {
            return false;
        }
    };

    const register = async (payload: RegisterPayload): Promise<RegisterResult> => {
        const { data } = await api.post<RegisterResult>(AUTH_ENDPOINTS.REGISTER, { ...payload, lang: getLang() });
        return data;
    };

    const requestPasswordReset = async (identifier: string): Promise<void> => {
        await api.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, { identifier, lang: getLang() });
    };

    const resetPasswordWithToken = async (token: string, newPassword: string): Promise<void> => {
        await api.post(AUTH_ENDPOINTS.RESET_PASSWORD, { token, newPassword });
    };

    /**
     * Change le mot de passe de l'utilisateur CONNECTE.
     *
     * Le userapp_id est exige par l'API : tc_identity verifie qu'il correspond
     * bien a l'appelant et refuse en 403 sinon, un utilisateur ne pouvant
     * changer que son propre mot de passe.
     *
     * Cote serveur, ce meme appel fait retomber le drapeau
     * must_change_password : c'est le passage oblige de tout changement.
     */
    const replaceOwnPassword = async (userappId: number, newPassword: string): Promise<void> => {
        await api.post(AUTH_ENDPOINTS.REPLACE_PASSWORD, {
            userapp_id: userappId,
            password: newPassword,
        });
    };

    const resendConfirmationEmail = async (email: string): Promise<void> => {
        await api.post(AUTH_ENDPOINTS.RESEND_CONFIRMATION, { email, lang: getLang() });
    };

    return {
        loginBasic,
        logout,
        me,
        patchMe,
        updateMetadataKey,
        isAuthenticated,
        register,
        requestPasswordReset,
        resetPasswordWithToken,
        replaceOwnPassword,
        resendConfirmationEmail,
    };
};
